<template>
  <div
    v-if="hasData"
    class="insights-work-time-expenditure-popover"
  >
    <div class="d-flex">
      <AppChartDonut
        :values="chartData"
        :sort="false"
        :size="chartSize"
        :inner-radius="0"
      />
      <AppChartLegend
        :values="legendData"
        orientation="vertical"
      />
    </div>
    <div class="tools mt-15 py-15">
      <div class="subtitle mb-15">Top Apps & Urls</div>
      <div class="tools-list d-flex flex-column">
        <template v-if="!columnData.tools_hidden">
          <div
            v-for="tool in toolsData"
            :key="tool.label"
            class="tool d-flex justify-content-between"
          >
            <div class="details">
              <span class="percentage bold">{{ tool.value }}%</span>
              <span class="name"> {{ tool.label }}</span>
              <AppChartLineStacked
                class="mt-5"
                :style="widthStyle(tool.value)"
                :has-axis-legend="false"
                :values="tool.chart"
              />
            </div>
            <span class="time">
              {{ tool.tracked }}
            </span>
          </div>
        </template>
        <img
          v-else
          class="data-privacy w-full"
          :src="dataPrivacyPlaceholder"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AppChartDonut from '~/components/AppChartDonut.vue';
import AppChartLegend from '~/components/AppChartLegend.vue';
import AppChartLineStacked from '~/components/AppChartLineStacked.vue';

import { kebabCase } from 'lodash-es';
import { formatDuration } from '~/utils/time_utils';

import { INSIGHTS, INSIGHTS_ASSETS as insightsAssets } from '~/app_constants';

const TOOLS_LIMIT = 5;

export default {
  name: 'InsightsWorkTimeExpenditurePopover',
  components: {
    AppChartDonut,
    AppChartLegend,
    AppChartLineStacked,
  },
  props: {
    columnData: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    chartSize: 55,
    productivityOptions: Object.freeze(INSIGHTS.productivityOptions),
    dataPrivacyPlaceholder: insightsAssets.ranking_placeholder[TOOLS_LIMIT].src,
  }),
  computed: {
    hasData() {
      return this.columnData && 'value' in this.columnData;
    },
    chartData() {
      return this.formatData(this.columnData);
    },
    legendData() {
      return this.formatData(this.columnData, false, true);
    },
    toolsData() {
      const tools = [...this.columnData.tools];

      return tools
        .sort((a, b) => b.value - a.value)
        .slice(0, TOOLS_LIMIT)
        .map((tool) => ({
          ...tool,
          tracked: this.timeSpent(tool.tracked),
          value: this.valuePercentage(tool.value),
          chart: this.formatData(tool, true),
        }));
    },
    maxToolsValue() {
      return Math.max(...this.toolsData.map((tool) => tool.value));
    },
  },
  methods: {
    formatData(array, kebab = false, roundValue = false) {
      return this.productivityOptions.map((option) => {
        const value = array[option.id]?.value >= 0 ? array[option.id]?.value : array[option.id];

        return {
          key: kebab ? kebabCase(option.id) : option.id,
          label: option.label,
          value: roundValue ? this.valuePercentage(value || 0) : (value || 0) * 100,
        };
      });
    },
    widthStyle(value) {
      return {
        width: `${(value * 100) / this.maxToolsValue}%`,
      };
    },
    timeSpent(time) {
      return formatDuration(time, { showSeconds: 'never' });
    },
    valuePercentage(value) {
      const percentage = value * 100;

      return +percentage.toFixed(1);
    },
  },
};
</script>
